/**
 * Composable для управления навигацией в админ-панели
 * 
 * Функциональность:
 * - Автоматическое сохранение активной вкладки в localStorage
 * - Восстановление состояния при перезагрузке страницы
 * - Валидация доступных вкладок с учётом прав пользователя
 * - Fallback на дефолтную вкладку при ошибках
 * - Поддержка вложенных разделов (расширяемость)
 * 
 * @author Senior Developer
 * @version 1.0.0
 */

import { ref, watch, onMounted, computed } from 'vue'
import { useAppStore } from '@/stores/useAppStore'

// Константы для конфигурации
const STORAGE_KEY = 'admin_navigation_state'
const DEFAULT_MENU_ID = 1
const STORAGE_VERSION = '1.0' // Для миграций при изменении структуры

/**
 * Конфигурация доступных пунктов меню
 * Каждый пункт содержит:
 * - id: уникальный идентификатор
 * - title: название раздела
 * - requiredRoles: массив ролей, имеющих доступ (пустой массив = доступно всем админам)
 * - isActive: флаг активности (для будущих фич)
 */
const MENU_ITEMS = [
	{
		id: 1,
		title: 'Дашборд',
		requiredRoles: [], // Доступно всем админам
		isActive: true,
	},
	{
		id: 2,
		title: 'Студенты',
		requiredRoles: [],
		isActive: true,
	},
	{
		id: 3,
		title: 'Преподаватели',
		requiredRoles: [],
		isActive: true,
	},
	{
		id: 4,
		title: 'Курсы',
		requiredRoles: [],
		isActive: true,
	},
	{
		id: 5,
		title: 'Аналитика',
		requiredRoles: [],
		isActive: true,
	},
	{
		id: 6,
		title: 'Отчёты',
		requiredRoles: [],
		isActive: true,
	},
	{
		id: 7,
		title: 'Настройки',
		requiredRoles: [],
		isActive: true,
	},
]

/**
 * Composable для управления навигацией админ-панели
 * 
 * @param {Object} options - Опции конфигурации
 * @param {number} options.defaultMenuId - ID меню по умолчанию (по умолчанию: 1)
 * @param {boolean} options.persistState - Сохранять ли состояние в localStorage (по умолчанию: true)
 * @param {string} options.storageKey - Ключ для localStorage (по умолчанию: 'admin_navigation_state')
 * @returns {Object} API для управления навигацией
 */
export function useAdminNavigation(options = {}) {
	const {
		defaultMenuId = DEFAULT_MENU_ID,
		persistState = true,
		storageKey = STORAGE_KEY,
	} = options

	const appStore = useAppStore()

	// Реактивное состояние активного меню
	const activeMenu = ref(defaultMenuId)

	// Computed для получения текущего пользователя
	const currentUser = computed(() => {
		const userRef = appStore.user
		return userRef?.value || userRef
	})

	/**
	 * Проверка доступности пункта меню для текущего пользователя
	 * 
	 * @param {number} menuId - ID пункта меню
	 * @returns {boolean} true если пункт доступен
	 */
	const isMenuItemAccessible = menuId => {
		const menuItem = MENU_ITEMS.find(item => item.id === menuId)

		// Если пункт меню не найден - недоступен
		if (!menuItem) {
			console.warn(`[useAdminNavigation] Menu item ${menuId} not found`)
			return false
		}

		// Если пункт неактивен - недоступен
		if (!menuItem.isActive) {
			console.warn(`[useAdminNavigation] Menu item ${menuId} is inactive`)
			return false
		}

		// Если не требуются специальные роли - доступен всем админам
		if (!menuItem.requiredRoles || menuItem.requiredRoles.length === 0) {
			return true
		}

		// Проверяем наличие требуемых ролей у пользователя
		const user = currentUser.value
		if (!user || !user.roles || !Array.isArray(user.roles)) {
			console.warn('[useAdminNavigation] User roles not found')
			return false
		}

		// Пользователь должен иметь хотя бы одну из требуемых ролей
		const hasRequiredRole = menuItem.requiredRoles.some(role =>
			user.roles.includes(role)
		)

		if (!hasRequiredRole) {
			console.warn(
				`[useAdminNavigation] User doesn't have required roles for menu ${menuId}`,
				{
					required: menuItem.requiredRoles,
					userRoles: user.roles,
				}
			)
		}

		return hasRequiredRole
	}

	/**
	 * Получение списка доступных пунктов меню для текущего пользователя
	 * 
	 * @returns {Array} Массив доступных пунктов меню
	 */
	const getAccessibleMenuItems = () => {
		return MENU_ITEMS.filter(item => isMenuItemAccessible(item.id))
	}

	/**
	 * Валидация ID меню
	 * Проверяет существование и доступность пункта меню
	 * 
	 * @param {number} menuId - ID для валидации
	 * @returns {boolean} true если ID валиден
	 */
	const isValidMenuId = menuId => {
		// Проверка типа
		if (typeof menuId !== 'number' || !Number.isInteger(menuId)) {
			console.warn('[useAdminNavigation] Invalid menu ID type:', typeof menuId)
			return false
		}

		// Проверка существования и доступности
		return isMenuItemAccessible(menuId)
	}

	/**
	 * Получение названия раздела меню по ID
	 * 
	 * @param {number} menuId - ID пункта меню
	 * @returns {string} Название раздела
	 */
	const getMenuTitle = menuId => {
		const menuItem = MENU_ITEMS.find(item => item.id === menuId)
		return menuItem?.title || 'Раздел'
	}

	/**
	 * Сохранение состояния навигации в localStorage
	 * 
	 * @param {number} menuId - ID активного меню
	 */
	const saveNavigationState = menuId => {
		if (!persistState) {
			return
		}

		try {
			const user = currentUser.value
			const userId = user?.id || user?.email || 'default'

			// Создаём объект состояния с метаданными
			const state = {
				version: STORAGE_VERSION,
				userId, // Привязка к пользователю
				activeMenuId: menuId,
				timestamp: Date.now(),
			}

			// Сохраняем в localStorage с префиксом пользователя
			const key = `${storageKey}_${userId}`
			localStorage.setItem(key, JSON.stringify(state))

			console.log('[useAdminNavigation] Navigation state saved:', {
				userId,
				menuId,
				menuTitle: getMenuTitle(menuId),
			})
		} catch (error) {
			console.error('[useAdminNavigation] Failed to save navigation state:', error)
			// Не прерываем работу приложения при ошибке сохранения
		}
	}

	/**
	 * Восстановление состояния навигации из localStorage
	 * 
	 * @returns {number|null} ID восстановленного меню или null
	 */
	const restoreNavigationState = () => {
		if (!persistState) {
			return null
		}

		try {
			const user = currentUser.value
			const userId = user?.id || user?.email || 'default'

			// Пытаемся получить сохранённое состояние
			const key = `${storageKey}_${userId}`
			const savedState = localStorage.getItem(key)

			if (!savedState) {
				console.log('[useAdminNavigation] No saved navigation state found')
				return null
			}

			// Парсим сохранённое состояние
			const state = JSON.parse(savedState)

			// Валидация версии (для будущих миграций)
			if (state.version !== STORAGE_VERSION) {
				console.warn(
					'[useAdminNavigation] Navigation state version mismatch, clearing...',
					{
						saved: state.version,
						current: STORAGE_VERSION,
					}
				)
				localStorage.removeItem(key)
				return null
			}

			// Валидация ID меню
			if (!isValidMenuId(state.activeMenuId)) {
				console.warn(
					'[useAdminNavigation] Saved menu ID is invalid or inaccessible:',
					state.activeMenuId
				)
				return null
			}

			console.log('[useAdminNavigation] Navigation state restored:', {
				userId,
				menuId: state.activeMenuId,
				menuTitle: getMenuTitle(state.activeMenuId),
				savedAt: new Date(state.timestamp).toLocaleString(),
			})

			return state.activeMenuId
		} catch (error) {
			console.error(
				'[useAdminNavigation] Failed to restore navigation state:',
				error
			)
			return null
		}
	}

	/**
	 * Установка активного пункта меню с валидацией
	 * 
	 * @param {number} menuId - ID пункта меню
	 * @returns {boolean} true если установка успешна
	 */
	const setActiveMenu = menuId => {
		// Валидация ID
		if (!isValidMenuId(menuId)) {
			console.error('[useAdminNavigation] Cannot set invalid menu ID:', menuId)
			appStore.showWarning(
				'Раздел недоступен или не существует',
				'Навигация'
			)
			return false
		}

		// Устанавливаем активное меню
		activeMenu.value = menuId

		// Сохраняем состояние (автоматически через watcher, но можно и явно)
		saveNavigationState(menuId)

		console.log('[useAdminNavigation] Active menu changed:', {
			menuId,
			menuTitle: getMenuTitle(menuId),
		})

		return true
	}

	/**
	 * Сброс состояния навигации к дефолтному значению
	 */
	const resetNavigation = () => {
		console.log('[useAdminNavigation] Resetting navigation to default')
		setActiveMenu(defaultMenuId)
	}

	/**
	 * Очистка сохранённого состояния из localStorage
	 */
	const clearSavedState = () => {
		try {
			const user = currentUser.value
			const userId = user?.id || user?.email || 'default'
			const key = `${storageKey}_${userId}`

			localStorage.removeItem(key)
			console.log('[useAdminNavigation] Saved state cleared')
		} catch (error) {
			console.error('[useAdminNavigation] Failed to clear saved state:', error)
		}
	}

	// Автоматическое сохранение при изменении activeMenu
	watch(
		activeMenu,
		newMenuId => {
			saveNavigationState(newMenuId)
		},
		{ immediate: false } // Не сохраняем при инициализации
	)

	// Восстановление состояния при монтировании
	onMounted(() => {
		const restoredMenuId = restoreNavigationState()

		if (restoredMenuId !== null) {
			// Используем восстановленное значение
			activeMenu.value = restoredMenuId
		} else {
			// Используем дефолтное значение и сохраняем его
			activeMenu.value = defaultMenuId
			saveNavigationState(defaultMenuId)
		}
	})

	// Публичный API
	return {
		// Состояние
		activeMenu,

		// Методы навигации
		setActiveMenu,
		resetNavigation,

		// Утилиты
		getMenuTitle,
		isValidMenuId,
		isMenuItemAccessible,
		getAccessibleMenuItems,

		// Управление состоянием
		saveNavigationState,
		restoreNavigationState,
		clearSavedState,

		// Константы
		MENU_ITEMS,
		DEFAULT_MENU_ID,
	}
}

